/**
 * RecipeDetailsPanel Component
 * Displays recipe ingredients, instructions, and metadata
 */

import { ReactNode } from 'react'
import { RecipeDetailResponse, Ingredient } from '@talktoast/shared'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { Loading } from '@talktoast/ui'
import NutritionInfoTooltip from './NutritionInfoTooltip'

// De-emphasizes parenthetical amounts like "(2 tbsp)" in instruction prose; digit-check guards narrative parens like "(optional)".
function renderInstructionWithAmounts(instruction: string): ReactNode {
  const parts = instruction.split(/(\([^)]*\))/)

  if (parts.length === 1) {
    return instruction
  }

  return (
    <>
      {parts.map((part, i) => {
        const isParenGroup = i % 2 === 1
        const isMeasurement = isParenGroup &&
          /^\(\s*\d[\d\s/]*\s*(tbsp|tsp|oz|lb|c|pkg|pt|qt|gal)?\s*\)$/i.test(part)
        if (isMeasurement) {
          return (
            <span key={i} className="text-xs text-gray-400 font-normal">
              {part}
            </span>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

interface RecipeDetailsPanelProps {
  recipeDetails: RecipeDetailResponse | null
  isLoading?: boolean
}

/**
 * Format ingredient object to readable string
 * Example: {amount: 2, unit: "cups", name: "flour"} => "2 cups flour"
 *
 * Guard: if the AI mistakenly includes a leading quantity in `name` (e.g. "1.5 lbs chicken"),
 * strip it so the quantity is never displayed twice alongside `amount`+`unit`.
 */
function formatIngredient(ingredient: Ingredient | string): string {
  // Handle legacy string format (if any)
  if (typeof ingredient === 'string') {
    return ingredient
  }

  // Format ingredient object
  const { amount, unit } = ingredient
  // Strip any leading number/fraction/unit that the AI may have included in the name field.
  // Pattern matches an optional leading number (int or decimal) optionally followed by a
  // measurement word (e.g. "1.5 lbs ", "3 tablespoons ", "2 ", "1/2 cup "), then captures
  // the rest as the true ingredient name + prep notes.
  const rawName = ingredient.name
  const nameWithoutLeadingQuantity = rawName.replace(
    /^\d+(?:[./]\d+)?\s*(?:lbs?|oz|g|kg|ml|l|cups?|tbsp|tsp|tablespoons?|teaspoons?|pieces?|cloves?|slices?|cans?|heads?|bunches?|pounds?|ounces?|grams?|kilograms?|millilitres?|litres?|pinch(?:es)?|handful|handfuls)?\s+/i,
    ''
  ).trim()

  // Guard: amount may be undefined/null when the AI omits it
  if (amount == null) {
    return nameWithoutLeadingQuantity
  }

  const formattedAmount = amount % 1 === 0 ? amount.toString() : amount.toFixed(2)

  if (unit && unit.toLowerCase() !== 'whole' && unit.toLowerCase() !== 'to taste') {
    return `${formattedAmount} ${unit} ${nameWithoutLeadingQuantity}`
  }

  return `${formattedAmount} ${nameWithoutLeadingQuantity}`
}

export default function RecipeDetailsPanel({ recipeDetails, isLoading }: RecipeDetailsPanelProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loading variant="spinner" size="sm" />
          <span>Loading recipe details...</span>
        </div>
      </div>
    )
  }

  if (!recipeDetails) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        Recipe details not available
      </div>
    )
  }

  const { recipe } = recipeDetails

  return (
    <div className="p-4 space-y-4 border-t border-gray-200">
      {/* Recipe Metadata */}
      <div className="flex flex-wrap gap-3 text-sm">
        {recipe.prepTime && (
          <div className="flex items-center gap-1.5 text-gray-600">
            <ICONS.time.clock className={getIconSize('sm')} />
            <span>{recipe.prepTime} min prep</span>
          </div>
        )}
        {recipe.cookTime && (
          <div className="flex items-center gap-1.5 text-gray-600">
            <ICONS.food.utensils className={getIconSize('sm')} />
            <span>{recipe.cookTime} min cook</span>
          </div>
        )}
        {recipe.servings && (
          <div className="flex items-center gap-1.5 text-gray-600">
            <ICONS.user.users className={getIconSize('sm')} />
            <span>{recipe.servings} servings</span>
          </div>
        )}
      </div>

      {/* Macros */}
      {recipe.nutrition ? (
        <div className="flex items-center gap-2 mt-2 mb-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-600 overflow-x-auto whitespace-nowrap scrollbar-hide">
            {recipe.nutrition.calories != null && <span><strong className="text-gray-800">{recipe.nutrition.calories}</strong> cal</span>}
            {recipe.nutrition.protein != null && <><span aria-hidden="true" className="text-gray-300">·</span><span><strong className="text-gray-800">{recipe.nutrition.protein}g</strong> pro</span></>}
            {recipe.nutrition.carbs != null && <><span aria-hidden="true" className="text-gray-300">·</span><span><strong className="text-gray-800">{recipe.nutrition.carbs}g</strong> carb</span></>}
            {recipe.nutrition.fat != null && <><span aria-hidden="true" className="text-gray-300">·</span><span><strong className="text-gray-800">{recipe.nutrition.fat}g</strong> fat</span></>}
            {recipe.nutrition.fiber != null && <><span aria-hidden="true" className="text-gray-300">·</span><span><strong className="text-gray-800">{recipe.nutrition.fiber}g</strong> fiber</span></>}
          </div>
          <NutritionInfoTooltip />
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic mt-2 mb-1">Macros unavailable</p>
      )}

      {/* Ingredients */}
      {recipe.ingredients && recipe.ingredients.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <ICONS.food.utensils className={getIconSize('sm')} />
            Ingredients
          </h4>
          <ul className="space-y-1.5">
            {recipe.ingredients.map((ingredient, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-gray-400 mt-0.5">•</span>
                <span>{formatIngredient(ingredient)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Instructions */}
      {recipe.instructions && recipe.instructions.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <ICONS.navigation.settings className={getIconSize('sm')} />
            Instructions
          </h4>
          <ol className="space-y-2">
            {recipe.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-toast-gold/10 text-toast-gold flex items-center justify-center text-xs font-semibold">
                  {index + 1}
                </span>
                <span className="flex-1">{renderInstructionWithAmounts(instruction)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Kitchen Tools */}
      {recipeDetails.kitchenTools && recipeDetails.kitchenTools.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-900 text-sm">Kitchen Tools</h4>
          <div className="flex flex-wrap gap-2">
            {recipeDetails.kitchenTools.map((tool, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cooking Tips */}
      {recipeDetails.cookingTips && recipeDetails.cookingTips.length > 0 && (
        <div className="space-y-2 p-3 bg-toast-gold/5 rounded-lg border border-toast-gold/20">
          <h4 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
            <span>💡</span>
            Tips from Toasty
          </h4>
          <ul className="space-y-1">
            {recipeDetails.cookingTips.map((tip, index) => (
              <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="text-toast-gold mt-0.5">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
